import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, FileText, Play, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const DeepAnalysisTester = () => {
  const [checkupId, setCheckupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  // Sample test data
  const sampleBusinessProfile = {
    user_id: "test-user-123",
    industry: "Retail",
    business_age: "2_to_5_years",
    entity_type: "LLC",
    annual_revenue: "250000-500000",
    employees: "1-5"
  };
  
  const sampleQuestionnaireAnswers = {
    reconciliation_frequency: "quarterly",
    bookkeeping_cadence: "quarterly",
    months_behind: 3,
    operating_margin: "unknown",
    owner_pay: "inconsistent",
    vendor_payments: "sometimes_late",
    separate_business_account: true,
    sales_tax_tracked: "unknown",
    payroll_filings_current: "yes",
    has_professional_bookkeeper: false
  };
  
  const sampleScoreBreakdown = {
    total_score: 48,
    basics_score: 15,
    business_score: 17,
    checks_score: 16
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/deep-analysis/generate`,
        {
          checkup_id: checkupId || 'test-checkup-' + Date.now(),
          business_profile: sampleBusinessProfile,
          questionnaire_answers: sampleQuestionnaireAnswers,
          score_breakdown: sampleScoreBreakdown
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegenerate = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/deep-analysis/regenerate`,
        {
          checkup_id: checkupId || 'test-checkup-' + Date.now(),
          business_profile: sampleBusinessProfile,
          questionnaire_answers: sampleQuestionnaireAnswers,
          score_breakdown: sampleScoreBreakdown
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/professional" className="flex items-center space-x-2 hover:opacity-80">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <span className="text-xl font-bold text-gray-900">Finance Vitals</span>
            </Link>
            <Link to="/professional" className="text-blue-600 hover:text-blue-800">
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Deep Analysis Tester</h1>
          <p className="text-gray-600">MVP: Test the Deep Analysis backend pipeline</p>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Controls</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Checkup ID (optional)
            </label>
            <input
              type="text"
              value={checkupId}
              onChange={(e) => setCheckupId(e.target.value)}
              placeholder="Leave blank for auto-generated ID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex space-x-4">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:bg-gray-400"
            >
              <Play className="w-5 h-5 mr-2" />
              {loading ? 'Generating...' : 'Generate Deep Analysis'}
            </button>
            
            {result && (
              <button
                onClick={handleRegenerate}
                disabled={loading}
                className="flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:bg-gray-400"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Regenerate
              </button>
            )}
          </div>

          {/* Test Data Info */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Test Data:</strong> Using sample score 48/100 (Fragile status)
            </p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>• Basics: 15/35 (Quarterly reconciliation, 3 months behind)</li>
              <li>• Business: 17/35 (Unknown margin, inconsistent owner pay)</li>
              <li>• Checks: 16/30 (Has separate account, unknown sales tax)</li>
            </ul>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success Display */}
        {result && (
          <div className="space-y-6">
            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                <div>
                  <h3 className="text-sm font-semibold text-green-800">Deep Analysis Generated</h3>
                  <p className="text-sm text-green-700">Analysis ID: {result.analysis_id}</p>
                </div>
              </div>
            </div>

            {/* Admin Narrative */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Admin Narrative
              </h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {result.admin_narrative}
              </p>
            </div>

            {/* Full JSON */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Full JSON Response</h2>
              <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs">
                {JSON.stringify(result.deep_analysis, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepAnalysisTester;
